import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, Row, Col, Modal, Badge } from 'react-bootstrap';
import { WarningIcon, EditIcon } from '../common/Icons';
import { productService } from '../../services/api';
import { Product, PaginatedResponse } from '../../types';
import { toast } from 'react-toastify';

const StockManagement: React.FC = () => {
  const [products, setProducts] = useState<PaginatedResponse<Product> | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'in' | 'out'>('in');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('adjustment');
  const [notes, setNotes] = useState('');

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getAll();
      setProducts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleAdjustStock = async () => {
    if (!selectedProduct || !quantity) return;

    try {
      await productService.adjustStock(selectedProduct.id, {
        type: adjustmentType,
        quantity: parseInt(quantity),
        reason,
        notes,
      });

      toast.success('Stock ajusté avec succès');
      setShowAdjustModal(false);
      setSelectedProduct(null);
      setQuantity('');
      setNotes('');
      fetchProducts();
    } catch (error) {
      console.error('Error adjusting stock:', error);
      toast.error('Erreur lors de l\'ajustement du stock');
    }
  };

  const getStockStatus = (product: Product) => {
    if (product.current_stock <= 0) {
      return <Badge bg="danger">Rupture</Badge>;
    } else if (product.current_stock <= product.minimum_stock) {
      return <Badge bg="warning">Stock faible</Badge>;
    } else {
      return <Badge bg="success">En stock</Badge>;
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Gestion du stock</h1>
      </div>

      <Card>
        <Card.Header>
          <h5>État du stock</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Produit</th>
                  <th>Stock actuel</th>
                  <th>Stock minimum</th>
                  <th>Statut</th>
                  <th>Emplacement</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products?.data.map((product) => (
                  <tr key={product.id}>
                    <td>{product.reference}</td>
                    <td>
                      {product.name}
                      {product.current_stock <= product.minimum_stock && (
                        <WarningIcon className="text-warning ms-2" />
                      )}
                    </td>
                    <td>{product.current_stock}</td>
                    <td>{product.minimum_stock}</td>
                    <td>{getStockStatus(product)}</td>
                    <td>{product.location || '-'}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowAdjustModal(true);
                        }}
                      >
                        <EditIcon className="me-1" />
                        Ajuster
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAdjustModal} onHide={() => setShowAdjustModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ajuster le stock - {selectedProduct?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type d'ajustement</Form.Label>
                  <Form.Select
                    value={adjustmentType}
                    onChange={(e) => setAdjustmentType(e.target.value as 'in' | 'out')}
                  >
                    <option value="in">Entrée (+)</option>
                    <option value="out">Sortie (-)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantité</Form.Label>
                  <Form.Control
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Raison</Form.Label>
              <Form.Select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="adjustment">Ajustement</option>
                <option value="loss">Perte</option>
                <option value="damage">Dommage</option>
                <option value="inventory">Inventaire</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes optionnelles..."
              />
            </Form.Group>

            <div className="alert alert-info">
              <strong>Stock actuel :</strong> {selectedProduct?.current_stock}<br />
              <strong>Nouveau stock :</strong> {
                selectedProduct ? (
                  adjustmentType === 'in' 
                    ? selectedProduct.current_stock + (parseInt(quantity) || 0)
                    : selectedProduct.current_stock - (parseInt(quantity) || 0)
                ) : 0
              }
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdjustModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleAdjustStock}>
            Ajuster le stock
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StockManagement;